<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <b-col
            cols="12"
            md="5"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <h3>Critical Success Factors/Barriers to implementation</h3>
          </b-col>
          <b-col cols="12" md="7">
            <div class="d-flex align-items-center justify-content-end">
              <b-button variant="primary" @click="addCSF()">
                <span class="text-nowrap">Add</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          :outlined="true"
          :fields="fields"
          responsive="sm"
          class="table table-sm"
          :items="filteredCSFs"
        >
          <template #cell(kpi)="data">
            <div style="font-size: 1rem;font-weight: bold;" class="p-25">
              <p>
                {{ data.item.kpi.title ? data.item.kpi.title : "-" }}
              </p>
            </div>
          </template>

          <template #cell(title)="data">
            <div style="font-size: 1rem;" class="p-25">
              <p>{{ data.value }}</p>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status == 'Active'"
              pill
              :variant="`light-success`"
              class="text-capitalize p-25"
            >
              {{ data.item.status }}
            </b-badge>

            <b-badge
              v-else
              pill
              :variant="`light-danger`"
              class="text-capitalize p-25"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div>
              <b-button
                variant="primary"
                class="mr-25 p-50"
                @click="editCSF(data.item.id)"
              >
                <i class="fa-solid fa-pen-to-square mr-25"></i>
                <span> Edit</span>
              </b-button>

              <b-button
                variant="primary"
                @click="deleteCSF(data.item.id)"
                class="p-50"
              >
                <span>{{
                  data.item.status === "Active" ? "Deactivate" : "Activate"
                }}</span>
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormRating,
} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormRating,
    vSelect,
    ToastificationContent,
  },

  setup() {
    const CSF_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(CSF_APP_STORE_MODULE_NAME))
      store.registerModule(CSF_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CSF_APP_STORE_MODULE_NAME))
        store.unregisterModule(CSF_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      csfs: [],
      filter: null,
      loading: false,
      fields: [
        {
          key: "kpi",
          sortable: true,
          class: "text-left",
          thStyle: "width:30%",
        },
        {
          key: "title",
          sortable: true,
          class: "text-left",
          thStyle: "width:40%",
        },
        {
          key: "status",
          sortable: false,
          class: "text-center",
          thStyle: "width:10%",
        },
        {
          key: "actions",
          sortable: false,
          class: "text-right",
          thStyle: "width:20%",
        },
      ],
    };
  },

  methods: {
    getCSFs() {
      this.loading = true;
      store
        .dispatch("settings/getAllCSFs")
        .then((res) => {
          this.csfs = res.data;
          this.loading = false;
        })
        .catch((error) => {
          this.$router.push({ name: "settings" });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "An error occurred",
              text: "Please try again later or contact support.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    editCSF(id) {
      this.$router.push({
        name: "settings-csf-edit",
        params: { id: id },
      });
    },

    deleteCSF(id) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to change status of this CSF?", {
          size: "sm",
          title: "Confirm Deletion",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.loading = true;
            const csf = this.csfs.find((met) => met.id === id);

            if (csf) {
              store
                .dispatch("settings/deleteCSF", csf)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successful",
                      text: "✔️ CSF status changed successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getCSFs();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    addCSF() {
      this.$router.push({ name: "settings-csf-add" });
    },
  },

  created() {
    this.getCSFs();
  },

  computed: {
    filteredCSFs() {
      if (!this.filter) {
        return this.csfs;
      } else {
        this.csfs = this.csfs.filter((met) => met.id !== undefined);

        this.csfs.forEach((met) => {
          met.editable = false;
        });

        const searchTerm = this.filter.toLowerCase();
        return this.csfs.filter((met) =>
          met.code.toLowerCase().includes(searchTerm)
        );
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
